/**
 * UI Manager for Full-Screen Chat
 *
 * ChatGPT-style centered conversation panel.
 * No Shadow DOM, no toggle button, always visible.
 */

import { formatTimestamp, scrollToBottom, isMobile } from '../shared/utils.js';

export class ChatUI {
  constructor(container, config = {}, stateManager = null) {
    this.container = container;
    this.config = config;
    this.backendConfig = null;
    this.stateManager = stateManager;

    // DOM elements
    this.chatContainer = null;
    this.messagesContainer = null;
    this.inputField = null;
    this.sendButton = null;
    this.typingIndicator = null;
    this.charCounter = null;
    this.currentQueueMessage = null;
    this.currentFormModal = null;
    this.currentInlineForm = null;

    // Callbacks
    this.onSendMessage = null;
  }

  /**
   * Update configuration from backend
   */
  updateBackendConfig(backendConfig) {
    this.backendConfig = backendConfig;

    // Update placeholder if input exists
    if (this.inputField && backendConfig.placeholders) {
      if (!this.inputField.disabled) {
        this.inputField.placeholder = backendConfig.placeholders.input;
      }
    }

    // Update character counter limits
    if (this.charCounter && backendConfig.limits) {
      this.updateCharCounter();
    }
  }

  /**
   * Initialize UI
   */
  init() {
    console.log('[ChatUI] Initializing, container:', this.container);

    // Clear any existing content
    this.container.innerHTML = '';

    // Create chat container
    this.chatContainer = document.createElement('div');
    this.chatContainer.className = 'chat-container';
    console.log('[ChatUI] Created chat-container element');

    // Create centered conversation panel
    const conversationPanel = document.createElement('div');
    conversationPanel.className = 'chat-panel';

    // Messages container
    this.messagesContainer = document.createElement('div');
    this.messagesContainer.className = 'chat-messages';
    this.messagesContainer.setAttribute('role', 'log');
    this.messagesContainer.setAttribute('aria-live', 'polite');
    this.messagesContainer.setAttribute('aria-label', 'Chat messages');

    // Typing indicator
    this.typingIndicator = document.createElement('div');
    this.typingIndicator.className = 'chat-typing';
    this.typingIndicator.style.display = 'none';
    this.typingIndicator.innerHTML = `
      <div class="chat-typing__dot"></div>
      <div class="chat-typing__dot"></div>
      <div class="chat-typing__dot"></div>
    `;

    // Input container
    const inputContainer = document.createElement('div');
    inputContainer.className = 'chat-input-container';

    // Input wrapper
    const inputWrapper = document.createElement('div');
    inputWrapper.className = 'chat-input-wrapper';

    // Textarea
    this.inputField = document.createElement('textarea');
    this.inputField.className = 'chat-input';
    this.inputField.placeholder = 'Connecting...';
    this.inputField.rows = 1;
    this.inputField.maxLength = 4000;

    // Character counter
    this.charCounter = document.createElement('div');
    this.charCounter.className = 'chat-char-counter';
    this.charCounter.textContent = '0 / 1000';

    // Send button
    this.sendButton = document.createElement('button');
    this.sendButton.className = 'chat-send-button';
    this.sendButton.setAttribute('aria-label', 'Send message');
    this.sendButton.innerHTML = `
      <svg viewBox="0 0 24 24" width="24" height="24">
        <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" fill="currentColor"/>
      </svg>
    `;
    this.sendButton.disabled = true;

    // Assemble input wrapper
    inputWrapper.appendChild(this.inputField);
    inputWrapper.appendChild(this.charCounter);

    // Assemble input container
    inputContainer.appendChild(inputWrapper);
    inputContainer.appendChild(this.sendButton);

    // Assemble conversation panel
    conversationPanel.appendChild(this.messagesContainer);
    conversationPanel.appendChild(this.typingIndicator);
    conversationPanel.appendChild(inputContainer);

    // Assemble chat container
    this.chatContainer.appendChild(conversationPanel);

    // Add to main container
    this.container.appendChild(this.chatContainer);

    // Attach event listeners
    this.attachEventListeners();

    // Focus input (desktop only)
    if (!isMobile()) {
      setTimeout(() => this.inputField.focus(), 300);
    }
  }

  /**
   * Attach event listeners
   */
  attachEventListeners() {
    // Send button - use pointer events (iOS keyboard fix v1.0.12)
    if ('PointerEvent' in window) {
      // FIX v1.0.12: mousedown prevents blur BEFORE it fires (mobile keyboard fix)
      this.sendButton.addEventListener('mousedown', (e) => {
        // Only prevent blur if button is disabled or field empty
        if (this.sendButton.disabled || this.inputField.value.trim() === '') {
          e.preventDefault(); // Prevents textarea blur = keyboard stays open
        }
        // If enabled + has text, allow blur (keyboard closes after send)
      });

      // Action handler (send message)
      this.sendButton.addEventListener('pointerup', () => {
        // FIX: No preventDefault() - breaks iOS Safari tap handling
        // Check if button disabled or field empty
        if (this.sendButton.disabled || this.inputField.value.trim() === '') {
          return; // Don't send
        }

        this.sendMessage();
      });
    } else {
      // Fallback for older browsers (same pattern)
      this.sendButton.addEventListener('mousedown', (e) => {
        if (this.sendButton.disabled || this.inputField.value.trim() === '') {
          e.preventDefault();
        }
      });

      this.sendButton.addEventListener('click', () => {
        // FIX: No preventDefault() - breaks iOS Safari tap handling
        if (this.sendButton.disabled || this.inputField.value.trim() === '') {
          return;
        }

        this.sendMessage();
      });
    }

    // Enter key to send (Shift+Enter for new line)
    this.inputField.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        this.sendMessage();
      }
    });

    // Character counter + auto-resize
    this.inputField.addEventListener('input', () => {
      this.updateCharCounter();
      this.autoResizeInput();
    });

    // Paste event handler (character limit security)
    this.inputField.addEventListener('paste', (e) => {
      const pastedText = (e.clipboardData || window.clipboardData).getData('text');
      const currentLength = this.inputField.value.length;
      const maxLength = this.backendConfig?.limits?.message_max || 4000;

      if (currentLength + pastedText.length > maxLength) {
        e.preventDefault();
        const overflow = (currentLength + pastedText.length) - maxLength;
        this.renderMessage(
          `Cannot paste: Content exceeds ${maxLength} character limit by ${overflow} characters.`,
          true,
          'error'
        );
      }
    });
  }

  /**
   * Send message
   */
  sendMessage() {
    const text = this.inputField.value.trim();

    if (!text || text.length === 0) {
      return;
    }

    // Check against backend's configured limit
    const maxLength = this.backendConfig?.limits?.message_max || 1000;
    if (text.length > maxLength) {
      this.renderMessage(`Message too long. Please keep it under ${maxLength} characters.`, true, 'error');
      return;
    }

    // Render user message
    this.renderMessage(text, false);

    // Clear input
    this.inputField.value = '';
    this.updateCharCounter();
    this.autoResizeInput();

    // Call callback
    if (this.onSendMessage) {
      this.onSendMessage(text);
    }
  }

  /**
   * Render message bubble
   */
  renderMessage(text, isBot = false, type = 'message') {
    const messageEl = document.createElement('div');
    messageEl.className = `chat-message chat-message--${isBot ? 'bot' : 'user'}`;

    if (type === 'error') {
      messageEl.classList.add('chat-message--error');
    } else if (type === 'system') {
      messageEl.classList.add('chat-message--system');
    }

    // Message content
    const contentEl = document.createElement('div');
    contentEl.className = 'chat-message__content';

    // Check for quick reply buttons (bot messages only)
    if (isBot && text.includes('||QUICK_REPLIES||')) {
      try {
        const [greetingText, buttonsJSON] = text.split('||QUICK_REPLIES||');

        // Render greeting text
        const lines = greetingText.trim().split('\\n');
        lines.forEach((line, index) => {
          if (index > 0) {
            contentEl.appendChild(document.createElement('br'));
          }
          contentEl.appendChild(document.createTextNode(line));
        });

        // Parse and render buttons
        const buttons = JSON.parse(buttonsJSON);
        if (buttons && Array.isArray(buttons) && buttons.length > 0) {
          const buttonContainer = document.createElement('div');
          buttonContainer.className = 'chat-quick-buttons';

          buttons.forEach((option, index) => {
            const btn = document.createElement('button');
            btn.textContent = option.text;
            // All buttons equal width, 2-per-row layout
            btn.className = 'chat-quick-button';
            btn.setAttribute('type', 'button');

            btn.onclick = () => {
              buttonContainer.remove();
              this.renderMessage(option.message, false);

              if (this.onSendMessage) {
                this.onSendMessage({
                  text: option.message,
                  metadata: {
                    type: 'quick_reply',
                    button_id: option.id,
                    button_text: option.text,
                    timestamp: new Date().toISOString()
                  }
                });
              }
            };

            buttonContainer.appendChild(btn);
          });

          contentEl.appendChild(buttonContainer);
        }
      } catch (e) {
        console.error('[Chat] Failed to parse quick reply buttons:', e);
        contentEl.textContent = text;
      }
    } else if (isBot && text.includes('[') && text.includes('](')) {
      // Parse markdown links (bot messages only)
      contentEl.appendChild(this.parseMarkdownLinks(text));
    } else {
      contentEl.textContent = text;
    }

    messageEl.appendChild(contentEl);

    // Timestamp
    const timeEl = document.createElement('div');
    timeEl.className = 'chat-message__time';
    timeEl.textContent = formatTimestamp();
    messageEl.appendChild(timeEl);

    this.messagesContainer.appendChild(messageEl);

    // Don't auto-scroll the first message (greeting) to keep it visible at top
    // First bot message is styled as greeting via CSS :first-child selector
    const messageCount = this.messagesContainer.querySelectorAll('.chat-message').length;
    if (messageCount > 1) {
      scrollToBottom(this.messagesContainer);
    }

    return messageEl;
  }

  /**
   * Parse markdown links in text
   */
  parseMarkdownLinks(text) {
    const fragment = document.createDocumentFragment();
    const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
    let lastIndex = 0;
    let match;

    while ((match = linkRegex.exec(text)) !== null) {
      const [fullMatch, linkText, url] = match;
      const matchStart = match.index;

      // Add text before link
      if (matchStart > lastIndex) {
        fragment.appendChild(document.createTextNode(text.substring(lastIndex, matchStart)));
      }

      // Validate URL (must be https:// or tel:)
      if (url.startsWith('https://') || url.startsWith('tel:')) {
        const link = document.createElement('a');
        link.href = url;
        link.textContent = linkText;
        link.className = 'chat-message__link';

        if (url.startsWith('https://')) {
          link.target = '_blank';
          link.rel = 'noopener noreferrer';
        }

        fragment.appendChild(link);
      } else {
        fragment.appendChild(document.createTextNode(fullMatch));
      }

      lastIndex = linkRegex.lastIndex;
    }

    // Add remaining text
    if (lastIndex < text.length) {
      fragment.appendChild(document.createTextNode(text.substring(lastIndex)));
    }

    return fragment;
  }

  /**
   * Show/hide typing indicator
   */
  showTypingIndicator(show = true) {
    this.typingIndicator.style.display = show ? 'flex' : 'none';
    if (show) {
      scrollToBottom(this.messagesContainer);
    }
  }

  /**
   * Enable/disable input
   */
  setInputEnabled(enabled) {
    this.inputField.disabled = !enabled;
    this.sendButton.disabled = !enabled;

    if (!enabled) {
      this.inputField.classList.add('chat-input--disabled');
      this.sendButton.classList.add('chat-send-button--disabled');
    } else {
      this.inputField.classList.remove('chat-input--disabled');
      this.sendButton.classList.remove('chat-send-button--disabled');
      this.inputField.readOnly = false;
    }
  }

  /**
   * Update character counter
   */
  updateCharCounter() {
    const length = this.inputField.value.length;
    const warningLimit = this.backendConfig?.limits?.char_warning || 800;
    const maxLimit = this.backendConfig?.limits?.char_max || 1000;

    this.charCounter.textContent = `${length} / ${maxLimit}`;

    if (length > maxLimit) {
      this.charCounter.className = 'chat-char-counter chat-char-counter--error';
      this.sendButton.disabled = true;
    } else if (length > warningLimit) {
      this.charCounter.className = 'chat-char-counter chat-char-counter--warning';
      this.sendButton.disabled = false;
    } else if (length === 0) {
      this.charCounter.className = 'chat-char-counter';
      this.sendButton.disabled = true;
    } else {
      this.charCounter.className = 'chat-char-counter';
      this.sendButton.disabled = false;
    }

    if (this.backendConfig?.limits?.message_max) {
      this.inputField.maxLength = this.backendConfig.limits.message_max;
    }
  }

  /**
   * Auto-resize input field
   */
  autoResizeInput() {
    this.inputField.style.height = 'auto';
    const maxHeight = 150; // Max ~6 lines
    const scrollHeight = Math.min(this.inputField.scrollHeight, maxHeight);
    this.inputField.style.height = `${scrollHeight}px`;
  }

  /**
   * Show connection status
   */
  showConnectionStatus(status) {
    switch (status) {
      case 'connecting':
        this.inputField.placeholder = this.backendConfig?.placeholders?.connecting || 'Connecting...';
        this.setInputEnabled(false);
        break;
      case 'connected':
        this.inputField.placeholder = this.backendConfig?.messages?.initializing || 'Initializing...';
        this.setInputEnabled(false);
        break;
      case 'ready':
        this.inputField.placeholder = this.backendConfig?.placeholders?.input || 'Type your message...';
        this.setInputEnabled(true);
        break;
      case 'disconnected':
        const disconnectMsg = this.backendConfig?.messages?.connection_lost || 'Connection lost. Reconnecting...';
        this.inputField.placeholder = disconnectMsg;
        this.setInputEnabled(false);
        break;
    }
  }

  /**
   * Show greeting message
   */
  showGreeting() {
    if (this.config.greeting) {
      const messageEl = this.renderMessage(this.config.greeting, true);
      // CSS :first-child selector handles full-width styling

      // Store greeting in state for message persistence
      if (this.stateManager) {
        this.stateManager.addMessage(this.config.greeting, true);
      }

      // Ensure greeting is visible at top (delay for button render)
      setTimeout(() => {
        this.messagesContainer.scrollTop = 0;
      }, 100);
    }
  }

  /**
   * Clear all messages
   */
  clearMessages() {
    this.messagesContainer.innerHTML = '';
  }

  /**
   * Remove last user message (for blocked/rejected messages)
   */
  removeLastUserMessage() {
    const messages = this.messagesContainer.querySelectorAll('.chat-message--user');
    if (messages.length > 0) {
      messages[messages.length - 1].remove();
    }
  }

  /**
   * Update queue position
   */
  updateQueuePosition(data) {
    const position = data.position || 0;
    const message = data.message;

    if (position > 0) {
      if (this.currentQueueMessage) {
        const contentEl = this.currentQueueMessage.querySelector('.chat-message__content');
        if (contentEl && message) {
          contentEl.textContent = message;
        }
      } else {
        if (message) {
          this.renderMessage(message, true, 'system');
          this.currentQueueMessage = this.messagesContainer.lastElementChild;
        }
      }
      this.setInputEnabled(false);

      if (this.backendConfig?.placeholders?.queue) {
        this.inputField.placeholder = this.backendConfig.placeholders.queue;
      }
    } else {
      if (this.currentQueueMessage) {
        this.currentQueueMessage.remove();
        this.currentQueueMessage = null;
      }
      this.setInputEnabled(true);

      if (this.backendConfig?.placeholders?.input) {
        this.inputField.placeholder = this.backendConfig.placeholders.input;
      }
    }
  }

  /**
   * Render property cards (search results with images)
   */
  renderPropertyCards(properties, totalCount) {
    if (!properties || properties.length === 0) {
      return;
    }

    console.log(`[UI] Rendering ${properties.length} property cards (total: ${totalCount})`);

    // Create header with total count
    const header = document.createElement('div');
    header.className = 'chat-cards-header';
    header.textContent = `Radau ${totalCount} objektų. Spauskite korteles:`;
    this.messagesContainer.appendChild(header);

    // Create container for all cards
    const cardsContainer = document.createElement('div');
    cardsContainer.className = 'chat-property-cards';

    // Render each property as a card
    properties.forEach((property) => {
      const card = this.createPropertyCard(property);
      if (card) {
        cardsContainer.appendChild(card);
      }
    });

    // Append to messages container
    this.messagesContainer.appendChild(cardsContainer);

    // Auto-scroll to show cards
    scrollToBottom(this.messagesContainer);
  }

  /**
   * Create a single property card element
   */
  createPropertyCard(property) {
    // Card wrapper (clickable)
    const card = document.createElement('a');
    card.href = property.link;
    card.target = '_blank';
    card.rel = 'noopener noreferrer';
    card.className = 'chat-property-card';

    // Image section (left side)
    if (property.image_url) {
      const imageContainer = document.createElement('div');
      imageContainer.className = 'chat-property-image';

      const img = document.createElement('img');
      img.src = property.image_url;
      img.alt = property.title;
      img.loading = 'lazy';

      // Handle image load errors
      img.onerror = () => {
        imageContainer.style.display = 'none';
      };

      imageContainer.appendChild(img);
      card.appendChild(imageContainer);
    }

    // Details section (right side)
    const details = document.createElement('div');
    details.className = 'chat-property-details';

    // Title
    const title = document.createElement('div');
    title.className = 'chat-property-title';
    title.textContent = property.title;
    details.appendChild(title);

    // Price (if available)
    if (property.price) {
      const price = document.createElement('div');
      price.className = 'chat-property-price';
      price.textContent = property.price;
      details.appendChild(price);
    }

    // Additional details (area, rooms, floor)
    if (property.details) {
      const info = document.createElement('div');
      info.className = 'chat-property-info';
      info.textContent = property.details;
      details.appendChild(info);
    }

    card.appendChild(details);

    return card;
  }

  /**
   * Show contact form modal
   */
  showContactForm(onSubmit) {
    // Check if form already exists
    if (this.currentFormModal) {
      console.log('[Form] Modal already exists');
      return;
    }

    // Create modal overlay
    const modal = document.createElement('div');
    modal.className = 'chat-form-modal';

    // Create modal content
    const modalContent = document.createElement('div');
    modalContent.className = 'chat-form-content';

    // Header with close button
    const headerContainer = document.createElement('div');
    headerContainer.className = 'chat-form-header-container';

    const header = document.createElement('h3');
    header.className = 'chat-form-header';
    header.textContent = this.backendConfig?.messages?.form_header_text || 'Kad galėtume pabendrauti, prašome pateikti savo duomenis';

    const closeButton = document.createElement('button');
    closeButton.className = 'chat-form-close-button';
    closeButton.setAttribute('aria-label', 'Close form');
    closeButton.textContent = '×';
    closeButton.onclick = () => {
      console.log('[Form] User closed form without submitting');
      this.hideContactForm();
    };

    headerContainer.appendChild(header);
    headerContainer.appendChild(closeButton);
    modalContent.appendChild(headerContainer);

    // Form
    const form = document.createElement('form');
    form.className = 'chat-contact-form';

    // Name field
    const nameGroup = this.createFormField('name', 'text', this.backendConfig?.messages?.form_name_label || 'Vardas', true);
    form.appendChild(nameGroup);

    // Phone field
    const phoneGroup = this.createFormField('phone', 'tel', this.backendConfig?.messages?.form_phone_label || 'Telefonas', true, '+370');
    form.appendChild(phoneGroup);

    // Email field
    const emailGroup = this.createFormField('email', 'email', this.backendConfig?.messages?.form_email_label || 'El. paštas', true);
    form.appendChild(emailGroup);

    // Privacy checkbox
    const privacyGroup = document.createElement('div');
    privacyGroup.className = 'chat-form-field chat-form-field--checkbox';

    const privacyCheckbox = document.createElement('input');
    privacyCheckbox.type = 'checkbox';
    privacyCheckbox.id = 'chat-privacy';
    privacyCheckbox.name = 'privacy';
    privacyCheckbox.className = 'chat-form-checkbox';
    privacyCheckbox.required = true;

    const privacyLabel = document.createElement('label');
    privacyLabel.htmlFor = 'chat-privacy';
    privacyLabel.className = 'chat-form-checkbox-label';

    // Parse privacy label with link
    const privacyText = this.backendConfig?.messages?.form_privacy_label || 'Sutinku su privatumo politika';
    const privacyUrl = this.backendConfig?.messages?.privacy_policy_url || 'https://vilniaus-turtas.lt/privatumo-politika';

    // Create link within label
    const privacyLink = document.createElement('a');
    privacyLink.href = privacyUrl;
    privacyLink.target = '_blank';
    privacyLink.rel = 'noopener noreferrer';
    privacyLink.textContent = privacyText;
    privacyLink.className = 'chat-privacy-link';

    privacyLabel.appendChild(privacyCheckbox);
    privacyLabel.appendChild(privacyLink);
    privacyGroup.appendChild(privacyLabel);

    form.appendChild(privacyGroup);

    // Error message container
    const errorContainer = document.createElement('div');
    errorContainer.className = 'chat-form-error';
    errorContainer.style.display = 'none';
    form.appendChild(errorContainer);

    // Submit button
    const submitButton = document.createElement('button');
    submitButton.type = 'submit';
    submitButton.className = 'chat-form-button';
    submitButton.textContent = this.backendConfig?.messages?.form_submit_button || 'Pateikti';
    form.appendChild(submitButton);

    // Form submit handler
    form.onsubmit = (e) => {
      e.preventDefault();

      // Hide previous errors
      errorContainer.style.display = 'none';

      // Get form data
      const formData = {
        name: form.elements.name.value.trim(),
        phone: form.elements.phone.value.trim(),
        email: form.elements.email.value.trim(),
        privacy_accepted: form.elements.privacy.checked,
        session_id: this.stateManager?.getSessionId() || null
      };

      // Basic client-side validation
      if (!formData.name || formData.name.length < 3) {
        this.showFormError(this.backendConfig?.messages?.form_error_name_too_short || 'Vardas per trumpas');
        return;
      }

      if (!formData.phone.startsWith('+370')) {
        this.showFormError(this.backendConfig?.messages?.form_error_phone_invalid || 'Telefonas turi prasidėti +370');
        return;
      }

      if (!formData.email.includes('@')) {
        this.showFormError(this.backendConfig?.messages?.form_error_email_invalid || 'Neteisingas el. pašto formatas');
        return;
      }

      if (!formData.privacy_accepted) {
        this.showFormError(this.backendConfig?.messages?.form_error_privacy_required || 'Privalote sutikti su privatumo politika');
        return;
      }

      // Disable form during submission
      submitButton.disabled = true;
      submitButton.textContent = 'Siunčiama...';

      // Call submit callback
      if (onSubmit) {
        onSubmit(formData);
      }
    };

    modalContent.appendChild(form);
    modal.appendChild(modalContent);

    // Add to document body
    document.body.appendChild(modal);
    this.currentFormModal = modal;

    // Focus first field
    setTimeout(() => {
      const nameInput = form.elements.name;
      if (nameInput) {
        nameInput.focus();
      }
    }, 100);

    return modal;
  }

  /**
   * Create form field
   */
  createFormField(name, type, label, required, placeholder) {
    const fieldGroup = document.createElement('div');
    fieldGroup.className = 'chat-form-field';

    const labelEl = document.createElement('label');
    labelEl.htmlFor = `chat-${name}`;
    labelEl.className = 'chat-form-label';
    labelEl.textContent = label;
    if (required) {
      const asterisk = document.createElement('span');
      asterisk.className = 'chat-form-required';
      asterisk.textContent = ' *';
      labelEl.appendChild(asterisk);
    }

    const input = document.createElement('input');
    input.type = type;
    input.id = `chat-${name}`;
    input.name = name;
    input.className = 'chat-form-input';
    input.required = required;
    if (placeholder) {
      input.placeholder = placeholder;
    }

    // iOS: 16px font to prevent zoom
    if (isMobile()) {
      input.style.fontSize = '16px';
    }

    fieldGroup.appendChild(labelEl);
    fieldGroup.appendChild(input);

    return fieldGroup;
  }

  /**
   * Hide contact form modal
   */
  hideContactForm() {
    if (this.currentFormModal) {
      this.currentFormModal.remove();
      this.currentFormModal = null;
      console.log('[Form] Modal hidden');
    }
  }

  /**
   * Show form error message
   */
  showFormError(message) {
    if (this.currentFormModal) {
      const errorContainer = this.currentFormModal.querySelector('.chat-form-error');
      if (errorContainer) {
        errorContainer.textContent = message;
        errorContainer.style.display = 'block';

        // Re-enable submit button
        const submitButton = this.currentFormModal.querySelector('.chat-form-button');
        if (submitButton) {
          submitButton.disabled = false;
          submitButton.textContent = this.backendConfig?.messages?.form_submit_button || 'Pateikti';
        }
      }
    }
  }

  /**
   * Render inline contact form inside chat bubble
   */
  renderInlineForm(onSubmit) {
    const formContainer = document.createElement('div');
    formContainer.className = 'chat-message chat-message--bot chat-message--form';
    formContainer.setAttribute('role', 'form');
    formContainer.setAttribute('aria-label', 'Contact form');

    const formContent = document.createElement('div');
    formContent.className = 'chat-inline-form-container';

    // Header
    const header = document.createElement('div');
    header.className = 'chat-inline-form-header';
    header.innerHTML = '📋 Palikite savo kontaktus:';
    formContent.appendChild(header);

    // Form element
    const form = document.createElement('form');
    form.className = 'chat-inline-form';
    form.onsubmit = (e) => {
      e.preventDefault();

      const formData = {
        name: form.querySelector('[name="name"]').value.trim(),
        phone: form.querySelector('[name="phone"]').value.trim(),
        email: form.querySelector('[name="email"]').value.trim(),
        privacy_accepted: form.querySelector('[name="privacy"]').checked
      };

      // Validate
      if (!this.validateInlineFormData(formData, form)) {
        return;
      }

      // Disable form after submission
      const submitBtn = form.querySelector('button[type="submit"]');
      submitBtn.disabled = true;
      submitBtn.textContent = 'Siunčiama...';

      // Disable all inputs
      form.querySelectorAll('input').forEach(input => input.disabled = true);

      onSubmit(formData);
    };

    // Name field
    form.appendChild(this.createInlineFormField('text', 'name', 'Vardas', true));

    // Phone field
    form.appendChild(this.createInlineFormField('tel', 'phone', 'Telefonas', true, '+370'));

    // Email field
    form.appendChild(this.createInlineFormField('email', 'email', 'El. paštas', true));

    // Privacy checkbox
    const privacyContainer = document.createElement('div');
    privacyContainer.className = 'chat-inline-form-privacy';

    const privacyCheckbox = document.createElement('input');
    privacyCheckbox.type = 'checkbox';
    privacyCheckbox.name = 'privacy';
    privacyCheckbox.required = true;
    privacyCheckbox.id = 'inline-privacy';

    const privacyLabel = document.createElement('label');
    privacyLabel.htmlFor = 'inline-privacy';
    const privacyUrl = this.backendConfig?.form?.privacy_policy_url || '#';
    privacyLabel.innerHTML = `Sutinku su <a href="${privacyUrl}" target="_blank">privatumo politika</a>`;

    privacyContainer.appendChild(privacyCheckbox);
    privacyContainer.appendChild(privacyLabel);
    form.appendChild(privacyContainer);

    // Submit button
    const submitBtn = document.createElement('button');
    submitBtn.type = 'submit';
    submitBtn.className = 'chat-inline-form-submit';
    submitBtn.textContent = 'Siųsti';
    form.appendChild(submitBtn);

    // Error message container
    const errorContainer = document.createElement('div');
    errorContainer.className = 'chat-inline-form-error';
    errorContainer.style.display = 'none';
    form.appendChild(errorContainer);

    formContent.appendChild(form);
    formContainer.appendChild(formContent);

    this.messagesContainer.appendChild(formContainer);
    scrollToBottom(this.messagesContainer);

    // Store reference for error display
    this.currentInlineForm = form;

    console.log('[Form] Inline form rendered');
  }

  /**
   * Create inline form field (simpler than modal version)
   */
  createInlineFormField(type, name, label, required, placeholder = '') {
    const fieldGroup = document.createElement('div');
    fieldGroup.className = 'chat-inline-form-field';

    const labelEl = document.createElement('label');
    labelEl.textContent = label;
    labelEl.htmlFor = `inline-${name}`;

    const input = document.createElement('input');
    input.type = type;
    input.name = name;
    input.id = `inline-${name}`;
    input.required = required;
    if (placeholder) input.placeholder = placeholder;

    // iOS: 16px font to prevent zoom
    if (isMobile()) {
      input.style.fontSize = '16px';
    }

    fieldGroup.appendChild(labelEl);
    fieldGroup.appendChild(input);

    return fieldGroup;
  }

  /**
   * Validate inline form data
   */
  validateInlineFormData(formData, formElement) {
    let errorMsg = '';

    if (!formData.name || formData.name.length < 3) {
      errorMsg = this.backendConfig?.form_errors?.name_too_short || 'Vardas per trumpas (min 3 simboliai)';
    } else if (!formData.phone || !formData.phone.startsWith('+370')) {
      errorMsg = this.backendConfig?.form_errors?.phone_invalid || 'Telefonas turi prasidėti +370';
    } else if (!formData.email || !formData.email.includes('@')) {
      errorMsg = this.backendConfig?.form_errors?.email_invalid || 'Neteisingas el. pašto formatas';
    } else if (!formData.privacy_accepted) {
      errorMsg = this.backendConfig?.form_errors?.privacy_required || 'Privalote sutikti su privatumo politika';
    }

    if (errorMsg) {
      this.showInlineFormError(errorMsg, formElement);
      return false;
    }

    return true;
  }

  /**
   * Show error in inline form
   */
  showInlineFormError(message, formElement) {
    const form = formElement || this.currentInlineForm;
    if (form) {
      const errorContainer = form.querySelector('.chat-inline-form-error');
      if (errorContainer) {
        errorContainer.textContent = message;
        errorContainer.style.display = 'block';

        // Re-enable submit button
        const submitBtn = form.querySelector('button[type="submit"]');
        if (submitBtn) {
          submitBtn.disabled = false;
          submitBtn.textContent = 'Siųsti';
        }

        // Re-enable all inputs
        form.querySelectorAll('input').forEach(input => input.disabled = false);
      }
    }
  }

  /**
   * Show success state in inline form
   */
  showInlineFormSuccess(message) {
    const form = this.currentInlineForm;
    if (form) {
      // Hide error if visible
      const errorContainer = form.querySelector('.chat-inline-form-error');
      if (errorContainer) {
        errorContainer.style.display = 'none';
      }

      // Update submit button to success state
      const submitBtn = form.querySelector('button[type="submit"]');
      if (submitBtn) {
        submitBtn.textContent = '✓ Išsiųsta!';
        submitBtn.classList.add('chat-inline-form-submit--success');
        submitBtn.disabled = true;

        // Hide all input fields after success
        const container = form.closest('.chat-inline-form-container');
        if (container) {
          const fields = container.querySelectorAll('.chat-inline-form-field, .chat-inline-form-privacy');
          fields.forEach(field => {
            field.style.display = 'none';
          });

          // Show success message
          const successMsg = document.createElement('div');
          successMsg.className = 'chat-inline-form-success';
          successMsg.innerHTML = `✓ ${message || 'Ačiū! Netrukus su jumis susisieksime.'}`;
          container.insertBefore(successMsg, form);
        }
      }

      console.log('[Form] Inline form success state shown');
    }
  }

  /**
   * Show resume session modal
   */
  showResumeModal(onResume, onNewChat) {
    // Create modal overlay
    const modal = document.createElement('div');
    modal.className = 'chat-resume-modal';

    // Create modal content
    const modalContent = document.createElement('div');
    modalContent.className = 'chat-modal-content';

    // Message text
    const messageText = document.createElement('p');
    messageText.textContent = this.backendConfig?.messages?.session_available || 'Your conversation is still saved';
    modalContent.appendChild(messageText);

    // Button container
    const buttonContainer = document.createElement('div');
    buttonContainer.className = 'chat-modal-buttons';

    // Resume button
    const resumeButton = document.createElement('button');
    resumeButton.className = 'chat-modal-button chat-modal-button--primary';
    resumeButton.textContent = this.backendConfig?.messages?.resume_button || 'Resume conversation';
    resumeButton.onclick = () => {
      console.log('[Resume] User chose to resume session');
      modal.remove();
      if (onResume) onResume();
    };

    // New chat button
    const newChatButton = document.createElement('button');
    newChatButton.className = 'chat-modal-button chat-modal-button--secondary';
    newChatButton.textContent = this.backendConfig?.messages?.new_chat_button || 'Start new chat';
    newChatButton.onclick = () => {
      console.log('[Resume] User chose to start fresh session');
      modal.remove();
      if (onNewChat) onNewChat();
    };

    buttonContainer.appendChild(resumeButton);
    buttonContainer.appendChild(newChatButton);
    modalContent.appendChild(buttonContainer);
    modal.appendChild(modalContent);

    // Add to document body
    document.body.appendChild(modal);

    return modal;
  }

  /**
   * Show rate limit countdown and retry button
   */
  showRateLimitCountdown(cooldownSeconds, message) {
    // Display error message
    this.renderMessage(message || 'Per daug bandymų prisijungti. Prašome palaukti.', true, 'error');

    // Disable input
    this.setInputEnabled(false);

    // Update placeholder with countdown
    let remainingSeconds = cooldownSeconds;
    const countdownInterval = setInterval(() => {
      remainingSeconds--;

      if (remainingSeconds > 0) {
        this.inputField.placeholder = `Bandykite po ${remainingSeconds} sekundžių...`;
      } else {
        // Countdown finished - show retry option
        clearInterval(countdownInterval);
        this.inputField.placeholder = 'Spauskite Enter arba Siųsti, kad prisijungtumėte iš naujo';
        this.setInputEnabled(true);

        // Show retry message
        this.renderMessage('Galite bandyti prisijungti iš naujo.', true, 'system');
      }
    }, 1000);
  }

  /**
   * Show form button (non-intrusive alternative to auto-modal)
   */
  showFormButton(onClick) {
    const buttonContainer = document.createElement('div');
    buttonContainer.className = 'chat-form-button-container';

    const button = document.createElement('button');
    button.className = 'chat-form-offer-button';
    button.textContent = '📧 Noriu konsultacijos';
    button.setAttribute('type', 'button');
    button.setAttribute('aria-label', 'Noriu konsultacijos su brokeriu');

    button.onclick = () => {
      buttonContainer.remove(); // Remove button after click
      onClick(); // Show form modal
    };

    buttonContainer.appendChild(button);
    this.messagesContainer.appendChild(buttonContainer);
    scrollToBottom(this.messagesContainer);

    console.log('[Form] Form button rendered');
  }
}
